import { FC, useCallback, useEffect, useState, useMemo, useContext } from "react";

import { Flex } from "antd";
import { AppContext } from "App";
import QuestionnaireButtonFooterV2 from "components/basic/Footer/QuestionnaireButtonFooterV2";
import ButtonLoading from "components/basic/Spinner/ButtonLoading";
import { QuestionType } from "enums/questionnaire";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { selectArrayQuestionState, setInnerArrayItemAnswers, setInnerArrayItemQuestions, setIsEditingInnerArrayItem } from "redux/reducers/arrayQuestion";
import { selectCurrentNavigation, setCurrentInnerArrayQuestionIndex, setStepLoading } from "redux/reducers/navigation";
import { IUser } from "types/auth";
import { ArrayAnswersType } from "types/questionnaire";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { findAndSetInnerArrayNext } from "utils/navigation";
import { checkDepends, formatAnswers, getNameFromQuestionAnswer, getOrdinalSuffix } from "utils/new-questionnaire";

import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { categories, Depend, Question } from "../../constants/questions";
import AmountQuestion from "../AmountQuestion/AmountQuestion";
import ArraySummarySection from "../ArraySummarySection/ArraySummarySection";
import ConditionalQuestionsRenderer from "../ConditionalQuestionsRenderer/ConditionalQuestionsRenderer";
import DateQuestion from "../DateQuestion/DateQuestion";
import PercentageQuestion from "../PercentageQuestion/PercentageQuestion";
import SelectOneOptionQuestion from "../SelectOneOptionQuestion/SelectOneOptionQuestion";
import SelectOneOptionQuestionV2 from "../SelectOneOptionQuestionV2/SelectOneOptionQuestionV2";
import DeleteModal from "../Shared/DeleteModal";
import TextQuestion from "../TextQuestion/TextQuestion";
import VehicleQuestion from "../VehicleQuestion/VehicleQuestion";
import YesNoQuestion from "../YesNoQuestion/YesNoQuestion";

type ArrayQuestionProps = {
    data: Question;
    onAnswer: (_question: string, _answer: any) => void;
    initialValue?: any;
    user: IUser;
    setHideProfileContinue?: (_value: boolean) => void;
    setContinueDisabled: (_value: boolean) => void;
    isProfileQuestion?: boolean;
    handlePrev: () => void;
};

const ArrayQuestion: FC<ArrayQuestionProps> = (
    {
        data,
        onAnswer,
        initialValue = [],
        user,
        setHideProfileContinue,
        setContinueDisabled,
        isProfileQuestion = false,
        handlePrev,
    }) => {
    const dispatch = useAppDispatch();

    const { isMobile } = useContext(AppContext);

    const isKeyboardOpen = useDetectKeyboardOpen();

    const [profileName, setProfileName] = useState("");
    const [editingIndex, setEditingIndex] = useState<number>(0);
    const [currentQuestion, setCurrentQuestion] = useState<Question>();
    const [currentItemName, setCurrentItemName] = useState<string>("");
    const [arrayItemsNames, setItemsNames] = useState<string[]>([]);
    const [deletionIndex, setDeletionIndex] = useState<number | null>(null);

    const { currentInnerArrayQuestionIndex, stepLoading, currentCategory } = useAppSelector(selectCurrentNavigation);
    const { isEditingInnerArrayItem, innerArrayAnswers } = useAppSelector(selectArrayQuestionState);

    const currentQuestionAnswered = useMemo(() => {
        if (!currentQuestion) return false;
        const { type, questions, question, answerRequired, isItemName } = currentQuestion;
        if (type === QuestionType.questionsList || type === QuestionType.conditionalQuestions) {
            const categoryAnswers = formatAnswers(innerArrayAnswers);

            const everySubQuestionAnswered = questions?.every(subQuestion => {
                if (subQuestion.depend?.length) {
                    const dependSatisfied = checkDepends(subQuestion.depend, categoryAnswers);

                    if (!dependSatisfied) {
                        // ✅ If dependencies are NOT met, consider it answered (skipped)
                        return true;
                    }

                    // ✅ If dependencies ARE met, check if it's answered
                    return categoryAnswers[subQuestion.question] !== undefined;
                }

                // ✅ If no dependencies, just check if the answer exists
                return innerArrayAnswers.some(answer => answer.question === subQuestion.question);
            });

            return everySubQuestionAnswered;
        }
        if (isItemName) {
            const isDuplicate = arrayItemsNames
                .filter(name => name !== currentItemName)
                .some(name => name.trim().toLowerCase() === currentItemName.trim().toLowerCase());
            if (isDuplicate) return false;
        }
        if (answerRequired) {
            const answer = innerArrayAnswers.find(q => q.question === question)?.answer;
            return !!answer;
        }
        if (!answerRequired) return true;
    }, [currentQuestion, innerArrayAnswers, arrayItemsNames, currentItemName]);

    const {
        isOpened: isDeletionModalOpened,
        open: handleDeletionModalOpen,
        close: handleDeletionModalClose,
    } = useModalOpener();

    const handleAddButtonClick = useCallback(async () => {
        setEditingIndex(initialValue.length ? initialValue.length : 0);
        setCurrentItemName("");
        dispatch(setIsEditingInnerArrayItem(true));
        dispatch(setCurrentInnerArrayQuestionIndex(0));
    }, [initialValue.length, dispatch]);

    const handleEditButtonClick = useCallback((index: number) => {
        const itemToEdit = initialValue[index];
        dispatch(setInnerArrayItemAnswers(itemToEdit.answers));
        setEditingIndex(index);
        setCurrentItemName(arrayItemsNames[index] || itemToEdit.item);
        dispatch(setIsEditingInnerArrayItem(true));
    }, [arrayItemsNames, dispatch, initialValue]);

    const updateAnswers = useCallback(
        (editIndex: number, arrayAns: ArrayAnswersType["answers"]) => {
            const isEdit = initialValue.length > 0 && editIndex < initialValue.length;

            const itemName = isEdit
                ? initialValue[editIndex]?.item || `Item ${editIndex + 1}`
                : `${data.itemName} ${initialValue.length + 1}`;

            const existingItem = isEdit ? initialValue[editIndex] : undefined;

            const updatedItem = {
                item: itemName,
                answers: arrayAns,
                isCompleted: existingItem?.isCompleted ?? false,
            };

            const updatedArray = isEdit
                ? initialValue.map((item: ArrayAnswersType, index: number) =>
                    (index === editIndex ? updatedItem : item)
                )
                : [...initialValue, updatedItem];

            setItemsNames(prevNames => {
                const newNames = [...prevNames];
                const name = getNameFromQuestionAnswer(
                    arrayAns,
                    data.questions,
                    isEdit ? editIndex : initialValue.length,
                    data.itemName
                );
                if (isEdit) {
                    newNames[editIndex] = name;
                } else {
                    newNames.push(name);
                }
                return newNames;
            });

            onAnswer(data.question, updatedArray);
        },
        [data, initialValue, onAnswer]
    );

    const removeUnmetDependentQuestions = useCallback((
        questions: Question[],
        answers: {question: string, answer: any, type: string, id: string}[],
        formattedAnswers: Record<string, any>,
        triggeringQuestionId: string
    ) => {
        for (const q of questions) {
            const isDependent = q.depend?.some((depend: Depend) => depend.question === triggeringQuestionId);
            if (isDependent && q.depend && !checkDepends(q.depend, formattedAnswers)) {
                const itemToRemove = answers.findIndex(a => a.question === q.question);
                if (itemToRemove !== -1) {
                    answers.splice(itemToRemove, 1);
                }
            }

            // Recursively process nested questions if they exist
            if (q.questions?.length) {
                removeUnmetDependentQuestions(q.questions, answers, formattedAnswers, triggeringQuestionId);
            }
        }
    }, []);

    const handleArrayQuestionAnswer = useCallback((question: string, answer: any, questionType?: string) => {
        const updatedAnswers = [...innerArrayAnswers];

        const existingIndex = updatedAnswers.findIndex(q => q.question === question);
        const updatedAnswer = { question, answer, type: questionType || currentQuestion?.type || "", id: currentQuestion?.id || "" };

        if (existingIndex > -1) {
            updatedAnswers[existingIndex] = updatedAnswer;
            const formattedArrayAnswers = formatAnswers(updatedAnswers);
            removeUnmetDependentQuestions(data.questions || [], updatedAnswers, formattedArrayAnswers, question);
        } else {
            updatedAnswers.push(updatedAnswer);
        }

        if (currentQuestion?.isItemName) {
            if (currentQuestion?.id === "SELF_EMPLOYMENT_CAR_MAKE_MODEL" || currentQuestion?.id === "CIS_CAR_MAKE_MODEL") {
                const year = answer.monthOfFirstRegistration;
                const yearAnswerValue = year.split("-")[0];
                setCurrentItemName(yearAnswerValue + " " + answer.make);
            } else {
                setCurrentItemName(answer);
            }
        }

        if (currentQuestion?.id) {
            updateAnswers(editingIndex, updatedAnswers);
        }

        dispatch(setInnerArrayItemAnswers(updatedAnswers));
    }, [setCurrentItemName, currentQuestion, innerArrayAnswers, editingIndex, updateAnswers, data.questions, dispatch, removeUnmetDependentQuestions]);

    const handleArrayQuestionsContinue = useCallback(async (answers?: { question: string, answer: string, type: string }[]) => {
        dispatch(setStepLoading(true));
        const timerValue = 800;

        const timer = setTimeout(async () => {
            if (data.questions) {
                updateAnswers(editingIndex, innerArrayAnswers);

                const { isArrayEnd } = findAndSetInnerArrayNext(
                    currentInnerArrayQuestionIndex,
                    data.questions,
                    answers ? [...answers, ...innerArrayAnswers] : innerArrayAnswers,
                    handleArrayQuestionAnswer,
                    dispatch
                );

                if (isArrayEnd) {
                    const updatedArray = initialValue.map((item: ArrayAnswersType, index: number) =>
                        (index === editingIndex
                            ? { ...item, isCompleted: true }
                            : item)
                    );

                    onAnswer(data.question, updatedArray);
                }
            }

            dispatch(setStepLoading(false));
        }, timerValue);

        return () => clearTimeout(timer);
    }, [dispatch, data.questions, data.question, updateAnswers, editingIndex, innerArrayAnswers, currentInnerArrayQuestionIndex, handleArrayQuestionAnswer, initialValue, onAnswer]);

    const handleDeleteButtonClick = useCallback((index: number) => {
        setDeletionIndex(index);
        handleDeletionModalOpen();
    }, [handleDeletionModalOpen]);

    const handleDeletionCancel = useCallback(() => {
        setDeletionIndex(null);
        handleDeletionModalClose();
    }, [handleDeletionModalClose]);

    const confirmDeleteClick = useCallback(() => {
        if (deletionIndex !== null) {
            const updatedArray = initialValue.filter((_item: ArrayAnswersType, i: number) => i !== deletionIndex);
            const updatedNames = arrayItemsNames.filter((_item: string, i: number) => i !== deletionIndex);

            setItemsNames(updatedNames);
            onAnswer(data.question, updatedArray.length === 0 ? undefined : updatedArray);
            handleDeletionModalClose();
        }
    }, [deletionIndex, initialValue, handleDeletionModalClose, arrayItemsNames, onAnswer, data.question]);

    useEffect(() => {
        dispatch(setInnerArrayItemQuestions(data?.questions || []));
        data.questions && setCurrentQuestion(data.questions[currentInnerArrayQuestionIndex]);
    }, [currentInnerArrayQuestionIndex, data.questions, dispatch]);

    useEffect(() => {
        if (isEditingInnerArrayItem) {
            setHideProfileContinue?.(true);

            if (data.itemTitle) {
                const itemIndex = (editingIndex ?? initialValue.length) + 1;
                const formattedItemTitle = currentItemName || `${getOrdinalSuffix(itemIndex)} ${data.itemTitle}`;
                setProfileName(formattedItemTitle);
            } else {
                setProfileName("");
            }
        } else {
            setHideProfileContinue?.(false);
            setProfileName("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentItemName, isEditingInnerArrayItem, isProfileQuestion]);

    useEffect(() => {
        if (initialValue?.length > 0 && arrayItemsNames.length === 0) {
            const initialNames = initialValue.map((item: ArrayAnswersType, index: number) =>
                getNameFromQuestionAnswer(
                    item.answers,
                    data.questions,
                    index,
                    data.itemName
                )
            );

            if (initialNames !== arrayItemsNames) {
                setItemsNames(initialNames);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    useEffect(() => {
        if (initialValue) {
            const arraysIncompleteItems = initialValue.some((item: ArrayAnswersType) => !item.isCompleted);
            setContinueDisabled(arraysIncompleteItems);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditingInnerArrayItem, arrayItemsNames]);

    useEffect(() => {
        return () => {
            setContinueDisabled(false);
            dispatch(setIsEditingInnerArrayItem(false));
            dispatch(setCurrentInnerArrayQuestionIndex(0));
            dispatch(setInnerArrayItemAnswers([]));
            dispatch(setInnerArrayItemQuestions([]));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={"new-questionnaire__array mt10"}>
            {profileName && (
                <div className={`new-questionnaire__array__profile-name mt0 ${isMobile ? "mb16" : ""}`}>{profileName}</div>
            )}
            {!isEditingInnerArrayItem && (
                <ArraySummarySection
                    data={data}
                    handleAddButtonClick={handleAddButtonClick}
                    handleEditButtonClick={handleEditButtonClick}
                    handleDeleteButtonClick={handleDeleteButtonClick}
                    profileAnswers={initialValue}
                    arrayItemsNames={arrayItemsNames}
                    currentCategoryTitle={categories[currentCategory].title}
                    user={user}
                />
            )}
            {isEditingInnerArrayItem && currentQuestion && (
                <div>
                    {currentQuestion.type === QuestionType.conditionalQuestions && (
                        <ConditionalQuestionsRenderer
                            data={currentQuestion}
                            currentProfileAnswers={innerArrayAnswers}
                            onAnswer={handleArrayQuestionAnswer}
                            handlePrev={handlePrev}
                            profileName={profileName}
                            user={user}
                        />
                    )}
                    {currentQuestion.type === QuestionType.dateQuestion && (
                        <DateQuestion
                            data={currentQuestion}
                            onAnswer={handleArrayQuestionAnswer}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}
                        />
                    )}
                    {currentQuestion.type === QuestionType.amountQuestion && (
                        <AmountQuestion
                            data={currentQuestion}
                            handlePrev={() => {}}
                            onAnswer={handleArrayQuestionAnswer}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}
                            arrayAnswers={innerArrayAnswers}
                        />
                    )}
                    {currentQuestion.type === QuestionType.percentageQuestion && (
                        <PercentageQuestion
                            data={currentQuestion}
                            onAnswer={handleArrayQuestionAnswer}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}
                        />
                    )}
                    {currentQuestion.type === QuestionType.textQuestion && (
                        <TextQuestion
                            data={currentQuestion}
                            onAnswer={handleArrayQuestionAnswer}
                            arrayItemsNames={arrayItemsNames}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}
                        />
                    )}
                    {currentQuestion.type === QuestionType.selectOneOptionQuestion && (
                        <SelectOneOptionQuestion
                            user={user}
                            data={currentQuestion}
                            onAnswer={handleArrayQuestionAnswer}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}
                        />
                    )}
                    {currentQuestion.type === QuestionType.vehiclesQuestion && (
                        <VehicleQuestion
                            categoryAnswers={innerArrayAnswers}
                            data={currentQuestion}
                            onAnswer={handleArrayQuestionAnswer}
                        />
                    )}
                    {currentQuestion.type === QuestionType.yesNoQuestion && (
                        <YesNoQuestion
                            data={currentQuestion}
                            onAnswer={handleArrayQuestionAnswer}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}

                        />
                    )}
                    {currentQuestion.type === QuestionType.selectOneOptionQuestionV2 && (
                        <SelectOneOptionQuestionV2
                            data={currentQuestion}
                            handlePrev={() => {}}
                            onAnswer={handleArrayQuestionAnswer}
                            initialValue={innerArrayAnswers.find(q => q.question === currentQuestion.question)?.answer}
                        />
                    )}
                    <div className={`${isKeyboardOpen ? "" : "mt100"}`}>
                        <QuestionnaireButtonFooterV2>
                            <Flex justify="space-between" align="center" className="w100 p20" gap={12}>
                                <button
                                    className={"shared__secondary-button"}
                                    onClick={() => {
                                        handlePrev();
                                    }}
                                >
                                    Back
                                </button>
                                <button
                                    className={`shared__primary-button maxWidth160px ${currentQuestionAnswered && !stepLoading ? "" : "disabled"}`}
                                    onClick={() => {
                                        if (currentQuestionAnswered && !stepLoading) {
                                            handleArrayQuestionsContinue();
                                        }
                                    }}
                                >
                                    {stepLoading ? <ButtonLoading/>
                                        : <span>{"Next"}</span>}
                                </button>
                            </Flex>
                        </QuestionnaireButtonFooterV2>
                    </div>
                </div>
            )}
            {isDeletionModalOpened && (
                <DeleteModal
                    isOpen={isDeletionModalOpened}
                    onCancel={handleDeletionCancel}
                    onConfirm={confirmDeleteClick}
                    closeable
                    data={{
                        title: `Deleting this ${data.itemName}?`,
                        text: `Are you sure you want to delete this ${data.itemName}? This action cannot be undone.`,
                        confirmButtonText: `Yes, delete this ${data.itemName}`,
                    }}
                />
            )}
        </div>
    );
};

export default ArrayQuestion;
